import {
  Alert,
  Button,
  Empty,
  Flex,
  Input,
  Segmented,
  Table,
  Typography,
} from 'antd';
import { FC, useMemo, useState } from 'react';

import { ReloadOutlined } from '@ant-design/icons';
import { useAdminBalancePortfolioController_findDiscrepancies } from '@api-client/generated/AdminBalancePortfolioController/findDiscrepancies';
import { getApiErrorMessage } from '@utils';

import IntervalWalk from './IntervalWalk';
import PortfolioSummary from './PortfolioSummary';
import { DiscrepancyRow, getColumns } from './columns';
import * as S from './styled';

const { Text } = Typography;

/**
 * The windows offered. A fortnight is the default because it is what the reconciliation's own lookback
 * was; a week is for reading a fresh problem without three weeks of intervals around it; and ninety
 * days is where a GoCardless connection's transaction backfill runs out, so nothing wider could be
 * compared against a history we hold. The API refuses anything outside 2..90 for the same reason.
 */
const WINDOW_CHOICES = [7, 14, 30, 90];

const DEFAULT_WINDOW_DAYS = 14;

/**
 * Which clients have a balance discrepancy, and when it started.
 *
 * The per-company Accounts tab already shows the reconciliation, but only to somebody who has already
 * opened that company — so a discrepancy is invisible until suspected, and an accountant carrying 138
 * companies has no way to ask the question the other way round. This is that way round: one list
 * across the whole estate, oldest discrepancy first, each row saying which interval it began in and
 * whether it looks like a transaction dated a day off or one that never arrived at all.
 *
 * The rows are a worklist, not a census — the counts above them are the census, and they matter as
 * much as the rows do. Seven in ten connected accounts cannot be checked at all, so a list of three
 * would otherwise read as three problems in the entire portfolio.
 *
 * Everything on the page is relative to a window, which is why the window is a control and not a
 * constant. A fortnight cannot show a discrepancy that started three weeks ago; ninety days buries a
 * fresh one under seventy intervals. And whatever the window, each row carries how far out its opening
 * balance already was — otherwise widening the window looks like discrepancies appearing and narrowing
 * it looks like them being fixed.
 */
const BalanceDiscrepancies: FC = () => {
  const [windowDays, setWindowDays] = useState(DEFAULT_WINDOW_DAYS);
  // The window is part of the generated hook's params, so it is part of the query key too — otherwise
  // switching it would leave the previous answer on screen under the new selection.
  const { data, isLoading, error, refetch } =
    useAdminBalancePortfolioController_findDiscrepancies({
      params: { windowDays },
    });
  const [search, setSearch] = useState('');

  const columns = useMemo(() => getColumns(), []);

  // Filtered on the client: the whole estate's open discrepancies are a short list by construction,
  // and the ordering by age is the point — paging or a server round trip per keystroke would both
  // work against reading it top to bottom.
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term || !data) {
      return data?.accounts ?? [];
    }

    return data.accounts.filter(
      (row) =>
        row.companyName?.toLowerCase().includes(term) ||
        row.accountName?.toLowerCase().includes(term)
    );
  }, [data, search]);

  const windowPicker = (
    <Flex align="center" gap={8}>
      <Text type="secondary">Window</Text>
      <Segmented
        options={WINDOW_CHOICES.map((days) => ({
          label: `${days} days`,
          value: days,
        }))}
        value={windowDays}
        onChange={(value) => setWindowDays(Number(value))}
      />
    </Flex>
  );

  if (error) {
    return (
      <Alert
        showIcon
        type="error"
        message="Could not load the balance discrepancies"
        description={getApiErrorMessage(
          error,
          'If it keeps failing, the cross-company mirror may not be reachable.'
        )}
        action={
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
          >
            Try again
          </Button>
        }
      />
    );
  }

  return (
    <Flex gap={24} vertical>
      <Flex align="center" gap={16} justify="space-between" wrap="wrap">
        <S.Heading>Balance discrepancies</S.Heading>
        {windowPicker}
      </Flex>

      {data && <PortfolioSummary summary={data.summary} />}

      <Input.Search
        allowClear
        placeholder="Search by company or account"
        onChange={(event) => setSearch(event.target.value)}
      />

      <Table<DiscrepancyRow>
        columns={columns}
        dataSource={rows}
        loading={isLoading}
        pagination={false}
        rowKey={(row) => row.accountId}
        scroll={{ x: 1250 }}
        // The working is one click away rather than on a separate page: the pattern across intervals
        // is what tells the two causes apart, and a diagnosis nobody can check is a diagnosis nobody
        // trusts.
        expandable={{
          expandedRowRender: (row) => (
            <IntervalWalk
              accountId={row.accountId}
              companyId={row.companyId}
              currencyCode={row.currencyCode}
              intervals={row.intervals}
              opening={row.opening}
            />
          ),
          rowExpandable: (row) => row.intervals.length > 0,
        }}
        // An empty list is a real answer, but not the one it looks like: it says every account that
        // *could* be checked adds up over *this* window, which is a much narrower claim than "the
        // portfolio is fine". So the count that was not checked is repeated here, where it cannot be
        // missed, and the window is named beside it.
        locale={{
          emptyText: (
            <Empty
              description={
                <Flex gap={4} vertical>
                  <Text>
                    Every account that could be checked adds up over the last{' '}
                    {data?.summary.windowDays ?? windowDays} days.
                  </Text>
                  {data && data.summary.notReconcilable > 0 && (
                    <Text type="secondary">
                      {data.summary.notReconcilable} of{' '}
                      {data.summary.connectedAccounts} connected accounts could
                      not be checked at all.
                    </Text>
                  )}
                </Flex>
              }
            />
          ),
        }}
      />
    </Flex>
  );
};

export default BalanceDiscrepancies;
